// Phase 16 · Management Dashboard — KPI cards + charts + alerts + field-force
import { useEffect, useState } from "react";
import { RefreshCw, TrendingUp, Users, ShoppingCart, MapPin, Clock, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AIInsightsCard from "@/pages/AIInsightsCard";

function KpiCard({ label, value, icon: Icon, hint, testid }) {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-neutral-500 font-semibold">{label}</span>
        <Icon className="w-4 h-4 text-[#E5197F]" />
      </div>
      <div className="text-2xl font-bold text-neutral-900 mt-1">{value}</div>
      {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}

function SparkBar({ items, valueKey, labelKey, testid, color = "#E5197F" }) {
  const max = Math.max(1, ...items.map(i => Number(i[valueKey]) || 0));
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <div className="w-24 truncate text-neutral-700">{it[labelKey] || "—"}</div>
          <div className="flex-1 h-4 bg-neutral-100 rounded">
            <div className="h-full rounded" style={{ width: `${(Number(it[valueKey]) / max) * 100}%`, background: color }} />
          </div>
          <div className="w-20 text-right text-neutral-700 font-medium">₹{Number(it[valueKey]||0).toLocaleString("en-IN")}</div>
        </div>
      ))}
    </div>
  );
}

function TrendLine({ items }) {
  if (!items?.length) return <div className="text-xs text-neutral-400">No data</div>;
  const w = 600, h = 80, pad = 6;
  const maxV = Math.max(1, ...items.map(i => i.value));
  const pts = items.map((it, i) => {
    const x = pad + (i * (w - 2 * pad)) / Math.max(1, items.length - 1);
    const y = h - pad - ((it.value / maxV) * (h - 2 * pad));
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24">
      <polyline fill="none" stroke="#E5197F" strokeWidth="2" points={pts} />
      <polyline fill="rgba(229,25,127,0.08)" stroke="none" points={`${pts} ${w - pad},${h - pad} ${pad},${h - pad}`} />
    </svg>
  );
}

export default function ManagementDashboard() {
  const [kpis, setKpis] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [alertData, setAlertData] = useState(null);
  const [ff, setFf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const [k, c, a, f] = await Promise.all([
        api.get("/dashboard/kpis"), api.get("/dashboard/charts?days=30"),
        api.get("/dashboard/alerts"), api.get("/dashboard/field-force"),
      ]);
      setKpis(k.data); setChartData(c.data); setAlertData(a.data); setFf(f.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
    } finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, []);

  const refresh = async () => { setRefreshing(true); await load(); };

  if (loading) return (
    <div className="flex justify-center py-20 text-neutral-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
  );

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="mgmt-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Management Dashboard</h1>
          <p className="text-xs text-neutral-500 mt-1">{kpis?.date} · Refreshes every 60s</p>
        </div>
        <button onClick={refresh} disabled={refreshing}
                 data-testid="dashboard-refresh"
                 className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-neutral-200 text-xs">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard testid="kpi-sales-value"       label="Sales value (today)" icon={TrendingUp}  value={`₹${Number(kpis?.total_sales_value||0).toLocaleString("en-IN")}`} />
        <KpiCard testid="kpi-orders"            label="Orders (today)"       icon={ShoppingCart} value={kpis?.orders_count ?? 0} />
        <KpiCard testid="kpi-active-users"      label="Field users active"   icon={Users}      value={kpis?.active_field_users ?? 0} />
        <KpiCard testid="kpi-coverage"          label="Coverage %"           icon={MapPin}     value={`${kpis?.coverage_pct ?? 0}%`}
                  hint={`${kpis?.executed_visits ?? 0}/${kpis?.planned_visits ?? 0}`} />
        <KpiCard testid="kpi-approvals"         label="Pending approvals"    icon={Clock}      value={kpis?.pending_approvals ?? 0} />
      </div>

      {/* AI Insights */}
      <AIInsightsCard />

      {/* Alerts panel */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2" data-testid="alerts-panel">
        <div className="flex items-center gap-2 text-amber-800 text-sm font-semibold">
          <AlertTriangle className="w-4 h-4" /> Exceptions today
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div><span className="font-semibold text-amber-900">{alertData?.absentees ?? 0}</span> absent field users</div>
          <div><span className="font-semibold text-amber-900">{alertData?.zero_order_field ?? 0}</span> zero-order field</div>
          <div><span className="font-semibold text-amber-900">{alertData?.missed_beats ?? 0}</span> missed beats</div>
          <div><span className="font-semibold text-amber-900">{alertData?.aging_approvals ?? 0}</span> approvals aging &gt;{alertData?.aging_threshold_days || 3}d</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-4" data-testid="chart-sales-trend">
          <div className="text-sm font-semibold mb-2">Sales trend (30d)</div>
          <TrendLine items={chartData?.sales_trend || []} />
        </div>
        <div className="bg-white rounded-xl border p-4" data-testid="chart-zone">
          <div className="text-sm font-semibold mb-2">Zone-wise (30d)</div>
          <SparkBar items={chartData?.zone_bar || []} valueKey="value" labelKey="zone_code" />
        </div>
        <div className="bg-white rounded-xl border p-4" data-testid="chart-top5">
          <div className="text-sm font-semibold mb-2">Top 5 performers (this month)</div>
          <SparkBar items={chartData?.top5 || []} valueKey="value" labelKey="user_name" color="#10b981" />
        </div>
        <div className="bg-white rounded-xl border p-4" data-testid="chart-sku-mix">
          <div className="text-sm font-semibold mb-2">SKU mix (top 8, qty)</div>
          <SparkBar items={chartData?.sku_mix || []} valueKey="qty" labelKey="sku_code" color="#6366f1" />
        </div>
      </div>

      {/* Field-force live */}
      <div className="bg-white rounded-xl border p-4" data-testid="field-force-panel">
        <div className="text-sm font-semibold mb-2">Field force · {ff?.date} · {ff?.total || 0} users</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-neutral-500">
              <tr><th className="text-left py-1">User</th><th>Role</th><th>Checked in?</th><th>Visits</th><th>Orders</th><th>Value</th></tr>
            </thead>
            <tbody>
              {(ff?.items || []).slice(0, 20).map(r => (
                <tr key={r.user_id} className="border-t border-neutral-100"
                     data-testid={`ff-row-${r.user_id}`}>
                  <td className="py-1.5">{r.user_name}</td>
                  <td className="text-center">{r.role_code}</td>
                  <td className="text-center">
                    <span className={`inline-block h-2 w-2 rounded-full ${r.checked_in ? "bg-emerald-500" : "bg-neutral-300"}`} />
                  </td>
                  <td className="text-center">{r.visits_done}</td>
                  <td className="text-center">{r.orders_count}</td>
                  <td className="text-right">₹{Number(r.orders_value||0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
